/**
 * Module: WaitScreen
 * Role: Delivery wait screen (stage 2). Shows timeline and countdown.
 * Uses: tokens (P, S, T, G), flow-types (Checkpoint, SlotFlowRun)
 * Used by: FlowScreen (case 'WAIT')
 *
 * Design ref: ai/designs/miniapp/project/pantene-flow.jsx PF_Delivery (L728-815)
 *
 * L-250: WAIT state determined by kind==='WAIT' AND flow_run.status, NOT by date.
 * No auto-advance from frontend — backend controls WAIT→next transition.
 */

import type { CSSProperties } from 'react';
import type { Checkpoint, SlotFlowRun, CheckpointAttempt } from '../../../api/flow-types';
import { PFStepStrip } from '../atoms/PFStepStrip';
import { P, S, T } from '../../../styles/tokens';

interface WaitScreenProps {
  checkpoint: Checkpoint;
  flowRun: SlotFlowRun;
  checkpoints: Checkpoint[];
  attempt?: CheckpointAttempt;
  onCta?: () => void;
}

/** Parse auto_advance_after_ms from checkpoint metadata into remaining days estimate. */
function computeRemainingDays(checkpoint: Checkpoint): number | null {
  const ms = checkpoint.metadata?.auto_advance_after_ms;
  if (typeof ms !== 'number' || ms <= 0) return null;
  return Math.ceil(ms / (1000 * 60 * 60 * 24));
}

const timelineStages = [
  { key: 'ordered',  label: 'Заказано' },
  { key: 'packed',   label: 'Собрано' },
  { key: 'transit',  label: 'В пути' },
  { key: 'pvz',      label: 'В ПВЗ' },
  { key: 'received', label: 'Получено' },
];

// Active stage for WAIT = 'transit' (reasonable default for "товар едет")
const ACTIVE_INDEX = 2;

export function WaitScreen({ checkpoint, flowRun: _flowRun, checkpoints }: WaitScreenProps) {
  // L-250: flowRun.status available for future status-driven rendering.
  void _flowRun;
  const remainingDays = computeRemainingDays(checkpoint);

  const wrapStyle: CSSProperties = {
    padding: '0 16px 24px',
    minHeight: '100dvh',
    background: P.bg,
    display: 'flex',
    flexDirection: 'column',
  };

  const cardStyle: CSSProperties = {
    background: P.card,
    borderRadius: 16,
    padding: 16,
    border: `1px solid ${P.line}`,
    marginTop: 16,
  };

  return (
    <div style={wrapStyle} data-testid="wait-screen">
      {/* Step strip */}
      <div style={{ padding: '16px 0 12px' }}>
        <PFStepStrip position={checkpoint.position} checkpoints={checkpoints} />
      </div>

      {/* Title */}
      <div style={{ font: T.title2, color: S.label, marginTop: 8, lineHeight: 1.2 }}>
        📦 Товар едет
      </div>
      <div style={{ font: T.footnote, color: S.secondaryLabel, marginTop: 4 }}>
        Как только приедет — придёт push, появится кнопка «Снять видео»
      </div>

      {/* Timeline card */}
      <div style={cardStyle}>
        <div style={{ font: T.caption, color: S.secondaryLabel, fontWeight: 600, textTransform: 'uppercase' }}>
          СТАТУС ДОСТАВКИ
        </div>

        <div style={{ marginTop: 14, position: 'relative', paddingLeft: 22 }}>
          {/* Vertical track line */}
          <div style={{
            position: 'absolute', left: 5, top: 6, bottom: 6,
            width: 2, background: '#E8E3D4', borderRadius: 1,
          }} />

          {timelineStages.map((s, i) => {
            const done = i < ACTIVE_INDEX;
            const active = i === ACTIVE_INDEX;
            return (
              <div key={s.key} style={{ position: 'relative', paddingBottom: i === timelineStages.length - 1 ? 0 : 12 }}>
                {/* Dot */}
                <div style={{
                  position: 'absolute', left: -20, top: 3,
                  width: 12, height: 12, borderRadius: 6,
                  background: done ? P.success : active ? P.terracotta : P.card,
                  border: `2px solid ${done ? P.success : active ? P.terracotta : '#E8E3D4'}`,
                  boxShadow: active ? '0 0 0 5px rgba(226,114,91,0.20)' : 'none',
                }} />
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                  <div style={{
                    font: T.footnote,
                    color: done || active ? S.label : S.tertiaryLabel,
                    fontWeight: active ? 600 : 400,
                  }}>{s.label}</div>
                  <div style={{ font: T.caption, color: S.secondaryLabel }}>
                    {done ? '✓' : '—'}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Remaining time badge */}
      {remainingDays !== null && (
        <div style={{
          marginTop: 12,
          padding: '10px 14px',
          borderRadius: 14,
          background: 'rgba(53,107,107,0.08)',
          border: '0.5px solid rgba(53,107,107,0.15)',
          font: T.footnote,
          color: P.jade,
          fontWeight: 600,
          textAlign: 'center',
        }}>
          ⏳ Осталось ~{remainingDays} {daysWord(remainingDays)}
        </div>
      )}

      {/* Tips card */}
      <div style={{ ...cardStyle, marginTop: 14 }}>
        <div style={{ font: T.caption, fontWeight: 700, color: S.label, marginBottom: 8 }}>
          💡 Пока ждёшь — подготовься:
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <TipRow>Найди светлое место у окна для съёмки</TipRow>
          <TipRow>Подумай, что скажешь — посмотри ещё раз бриф</TipRow>
          <TipRow>Подключи мобильный интернет — видео ~50 МБ</TipRow>
        </div>
      </div>
    </div>
  );
}

function TipRow({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start', font: T.footnote, color: S.label }}>
      <span style={{ color: P.success, flexShrink: 0 }}>✓</span>
      <span>{children}</span>
    </div>
  );
}

/** Russian day declension: день/дня/дней */
function daysWord(n: number): string {
  const abs = Math.abs(n) % 100;
  const last = abs % 10;
  if (abs >= 11 && abs <= 19) return 'дней';
  if (last === 1) return 'день';
  if (last >= 2 && last <= 4) return 'дня';
  return 'дней';
}
